import contactBanner from '@/../../public/storage/images/contact-banner.webp';
import Banner from '@/components/Banner';
import InquiryForm from '@/components/InquiryForm';
import { Reveal, RevealGroup, RevealItem } from '@/components/Reveal';
import ShareDialog from '@/components/ShareDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/components/ui/card';
import Typography from '@/components/ui/typography';
import Band from '@/Layouts/Band';
import SectionLayout from '@/Layouts/SectionLayout';
import { ContactProps } from '@/types';
import { ExternalLink, Home, Mail, Phone } from 'lucide-react';

const Contact = ({ branches }: { branches: ContactProps[] }) => {
  return (
    <>
      <Banner
        image={contactBanner}
        width={1500}
        height={1000}
        alt="Telephone Banner"
        tagline="Connect with us"
        title="Contact Samuthrika Academy"
        description="Looking for a course, batch or branch near you? Contact Samuthrika Academy for current course availability, fees, schedules, learning mode and admission guidance. Tell us your preferred course and location so our team can help you choose the right program."
      >
        <div className="flex flex-col items-start gap-4">
          <a
            href="mailto:support@samuthrika.academy?subject=Support"
            onClick={() => window.dataLayer?.push({ event: 'email_click' })}
            target="_blank"
            rel="noreferrer"
            className="text-base underline decoration-1 underline-offset-4 transition-colors hover:text-primary md:text-lg"
          >
            support@samuthrika.academy
          </a>

          <ShareDialog
            url={window.location.href}
            title="Contact Samuthrika Academy"
            content="Looking for a course, batch or branch near you? Contact Samuthrika Academy for current course availability, fees, schedules, learning mode and admission guidance."
          />
        </div>
      </Banner>

      <Band tone="surface" pattern="lattice">
        <SectionLayout space="lg">
          <Reveal className="flex flex-col gap-3">
            <Typography.Eyebrow>Our branches</Typography.Eyebrow>
            <Typography.Display2 className="text-2xl md:text-3xl lg:text-4xl">
              Where to find us
            </Typography.Display2>
            <Typography.Body className="max-w-prose text-muted-foreground">
              Every branch runs the same courses with the same certification.
              Pick the one nearest you.
            </Typography.Body>
          </Reveal>

          <RevealGroup className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
            {branches.map((branch) => (
              <RevealItem key={branch.id} className="flex">
                <Card
                  id={branch.name.replaceAll(' ', '')}
                  className="flex h-full w-full scroll-mt-28 flex-col shadow-card"
                >
                  <CardHeader>
                    {/* h3, not Large's default h4: "Where to find us" above
                        is this section's h2 and nothing else sits between it
                        and each branch name. */}
                    <Typography.Large
                      as="h3"
                      className="flex flex-wrap items-center gap-2 capitalize"
                    >
                      {branch.name}
                      {branch.type === 'Head Office' && (
                        <Badge className="rounded-full shadow-none">
                          {branch.type}
                        </Badge>
                      )}
                    </Typography.Large>
                  </CardHeader>
                  <CardContent className="flex-1">
                    <div className="flex h-full flex-col items-start gap-y-4">
                      {branch.address && (
                        <div className="flex flex-row items-start gap-3">
                          <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-md bg-primary-muted">
                            <Home size={18} className="text-primary" />
                          </span>
                          <Typography.Subtle>
                            {branch.address}
                          </Typography.Subtle>
                        </div>
                      )}
                      {Boolean(branch.show_phone_no) && (
                        <div className="flex flex-row items-start gap-3">
                          <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-md bg-primary-muted">
                            <Phone size={18} className="text-primary" />
                          </span>
                          <Typography.Subtle className="flex flex-col">
                            <a
                              href={`tel:+91${branch.phone_no_1}`}
                              onClick={() =>
                                window.dataLayer?.push({
                                  event: 'phone_click',
                                  branch: branch.name,
                                })
                              }
                            >
                              {branch.phone_no_1}
                            </a>
                            {branch.phone_no_2 && (
                              <a
                                href={`tel:+91${branch.phone_no_2}`}
                                onClick={() =>
                                  window.dataLayer?.push({
                                    event: 'phone_click',
                                    branch: branch.name,
                                  })
                                }
                              >
                                {branch.phone_no_2}
                              </a>
                            )}
                          </Typography.Subtle>
                        </div>
                      )}
                      <div className="flex max-w-full flex-row items-start gap-3">
                        <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-md bg-primary-muted">
                          <Mail size={18} className="text-primary" />
                        </span>
                        <Typography.Subtle className="overflow-hidden text-ellipsis underline decoration-1 underline-offset-4">
                          <a
                            href={`mailto:${branch.email}?cc=support@samuthrika.academy&subject=Support`}
                            onClick={() =>
                              window.dataLayer?.push({
                                event: 'email_click',
                                branch: branch.name,
                              })
                            }
                            target="_blank"
                            rel="noreferrer"
                          >
                            {branch.email}
                          </a>
                        </Typography.Subtle>
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter>
                    <Button asChild variant="outline" className="w-full">
                      <a
                        href={branch.location_url}
                        onClick={() =>
                          window.dataLayer?.push({
                            event: 'get_directions',
                            branch: branch.name,
                          })
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Get directions
                        <ExternalLink size={16} className="ml-2" />
                      </a>
                    </Button>
                  </CardFooter>
                </Card>
              </RevealItem>
            ))}
          </RevealGroup>
        </SectionLayout>
      </Band>

      <Band tone="raised" seam>
        <SectionLayout
          id="inquiry"
          width="prose"
          space="lg"
          className="scroll-mt-28"
        >
          <div className="flex flex-col gap-3">
            <Typography.Eyebrow>Enquiries</Typography.Eyebrow>
            <Typography.Display2 className="text-2xl md:text-3xl lg:text-4xl">
              Send us a message
            </Typography.Display2>
            <Typography.Subtle>
              Got questions or ideas? Fill this in and we&apos;ll be in touch
              soon.
            </Typography.Subtle>
          </div>

          <InquiryForm />
        </SectionLayout>
      </Band>
    </>
  );
};

export default Contact;
